import {
  BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication,
} from '@azure/msal-browser'
import type { AccountInfo, Configuration } from '@azure/msal-browser'

/**
 * MSAL, authorisation-code + PKCE, PUBLIC CLIENT WITH NO SECRET. `04 §4.3`.
 *
 * <p>The two identifiers come from E-4 — the application registration — and are injected at build
 * time rather than compiled in, so the same bundle can face DEV and production. A missing one is a
 * startup refusal and not a silent fallback: an SPA that quietly authenticated against the wrong
 * tenant would be worse than one that did not start.</p>
 */
export interface AuthConfig {
  clientId: string
  tenantId: string
  scope: string
}

export function configFromEnvironment(): AuthConfig {
  const env = import.meta.env

  return {
    clientId: String(env['VITE_AUTH_CLIENT_ID'] ?? ''),
    tenantId: String(env['VITE_AUTH_TENANT_ID'] ?? ''),
    scope: String(env['VITE_AUTH_SCOPE'] ?? 'api://eia-bff/Engagements.ReadWrite'),
  }
}

export function msalConfiguration(config: AuthConfig): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,

      // THE DEEP LINK SURVIVES THE ROUND TRIP. Redirecting to `/` would land a user who followed a
      // gate notification on the workspace list instead of the gate they were told about, and a
      // notification whose link does not arrive where it said is worse than no notification.
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // SESSION STORAGE, NOT LOCAL. A token surviving in `localStorage` outlives the tab on a
      // shared machine; the cost is one silent re-acquisition per new tab, which is invisible.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  }
}

/** What a page needs from authentication, without knowing it is MSAL. */
export interface Authenticator {
  account: AccountInfo | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>

  /** A fresh access token. Silent until it cannot be. */
  token: () => Promise<string>
}

/**
 * The token acquisition, and the one branch that matters.
 *
 * <p><b>THE USER NEVER REFRESHES THE BROWSER TO RECOVER.</b> A silent-refresh failure shows the
 * signed-out indicator, RETAINS EVERYTHING ALREADY RECEIVED, and re-authenticates in place. That
 * is why this returns a rejected promise on interaction-required rather than navigating away:
 * navigating would discard the page's state, which is exactly what the requirement forbids.</p>
 */
export async function acquireToken(
  msal: PublicClientApplication, account: AccountInfo, scope: string,
): Promise<string> {
  try {
    const result = await msal.acquireTokenSilent({ scopes: [scope], account })
    return result.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
      // A POPUP, NOT A REDIRECT. The page keeps its conversation, its committed keys and its open
      // gate; the user signs in beside it and the stream reconnects where it left off.
      const result = await msal.acquireTokenPopup({ scopes: [scope], account })
      return result.accessToken
    }

    throw error
  }
}

/**
 * Whether this principal is in the access group.
 *
 * <p>The BFF is the authority and refuses with a 401 either way — this is a RENDERING decision, so
 * that a person who is signed in but not entitled sees the designed page (`AC-AUTH-2`) rather than a
 * generic error. An empty configured group means no group check, which is a deployment's decision
 * rather than a default.</p>
 */
export function inAccessGroup(account: AccountInfo | null, groupObjectId: string): boolean {
  if (groupObjectId.length === 0) return true
  if (!account) return false

  const groups = (account.idTokenClaims as { groups?: unknown } | undefined)?.groups
  return Array.isArray(groups) && groups.includes(groupObjectId)
}
